import React, { useId } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { FOCUS_RING_ONLY, FIELD_HELP_CLASS, FIELD_LABEL_ERROR_CLASS } from './FieldShell';

/**
 * A single radio. The app had none — every either/or choice was a hand-rolled
 * `<input type="radio">` with a bare `text-primary-600`, or (more often) a pair
 * of buttons pretending to be one.
 *
 * The native input keeps the roving-tabindex and arrow-key behaviour that a
 * button pair silently throws away; `accent-champagne` is what makes the checked
 * dot brand-coloured without redrawing the control.
 */

export interface RadioProps {
  /** Falls back to a generated id — never `Math.random()`, which breaks SSR/hydration. */
  id?: string;
  /** Shared across a group; what makes arrow-key navigation work. */
  name?: string;
  value: string;
  label: string;
  checked?: boolean;
  onChange?: (value: string) => void;
  /** Muted second line under the label. */
  description?: string;
  disabled?: boolean;
  /** Renders the label in danger tone; the group draws the message row. */
  error?: boolean;
  className?: string;
}

const Radio: React.FC<RadioProps> = ({
  id,
  name,
  value,
  label,
  checked,
  onChange,
  description,
  disabled,
  error,
  className,
}) => {
  const { t } = useTranslation();
  const generatedId = useId();
  const inputId = id || generatedId;
  const descriptionId = description ? `${inputId}-description` : undefined;

  return (
    <div className={clsx('flex items-start gap-2', disabled && 'opacity-60', className)}>
      <input
        id={inputId}
        type="radio"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        aria-describedby={descriptionId}
        onChange={() => onChange?.(value)}
        className={clsx(
          'mt-0.5 h-4 w-4 flex-shrink-0 accent-champagne border-hairline',
          FOCUS_RING_ONLY,
          disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        )}
      />
      <div className="min-w-0">
        <label
          htmlFor={inputId}
          className={clsx(
            'block text-sm leading-5',
            error ? FIELD_LABEL_ERROR_CLASS : 'text-fg-body',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          )}
        >
          {t(label)}
        </label>
        {description && (
          <p id={descriptionId} className={clsx('mt-0.5', FIELD_HELP_CLASS)}>
            {t(description)}
          </p>
        )}
      </div>
    </div>
  );
};

export default Radio;
